import { useState } from "react";
import { motion } from "framer-motion";

const API = process.env.REACT_APP_API_URL || "/api";

export default function Login({ onLogin }: { onLogin: (token: string, name: string, role: string) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password })
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || "Login failed"); return; }
      localStorage.setItem("token", data.token);
      localStorage.setItem("userName", data.name);
      localStorage.setItem("userRole", data.role);
      onLogin(data.token, data.name, data.role);
    } catch {
      setError("Cannot connect to server");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{background: "linear-gradient(135deg, #020817 0%, #0a1628 50%, #020817 100%)"}}>
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <motion.div key={i} className="absolute rounded-full opacity-10"
            style={{width: 200+i*80, height: 200+i*80, left:`${10+i*15}%`, top:`${5+i*12}%`,
              background: i%2===0 ? "radial-gradient(circle, #4f8ef7, transparent)" : "radial-gradient(circle, #a78bfa, transparent)"}}
            animate={{scale:[1,1.1,1], opacity:[0.05,0.12,0.05]}}
            transition={{duration:4+i, repeat:Infinity, ease:"easeInOut"}} />
        ))}
      </div>

      <motion.div initial={{opacity:0, y:30}} animate={{opacity:1, y:0}} transition={{duration:0.6}}
        className="relative w-full max-w-md mx-4 p-8 rounded-2xl border border-blue-900/40"
        style={{background: "rgba(10,22,40,0.85)", backdropFilter:"blur(20px)"}}>

        <div className="flex flex-col items-center mb-8">
          <img src="/reknew-logo.png" alt="ReKnew" style={{height:'40px',width:'auto'}} className="mb-2" />
          <h1 className="text-xl font-bold text-white">Cloud Assistant</h1>
          <p className="text-sm text-blue-400/70 mt-1">Sign in to your account</p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs text-blue-400/70 uppercase tracking-widest mb-1.5 block">Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleSubmit()}
              placeholder="you@reknew.ai"
              className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-gray-600 outline-none border border-blue-900/40 focus:border-blue-500/60 transition-colors"
              style={{background:"rgba(255,255,255,0.04)"}} />
          </div>
          <div>
            <label className="text-xs text-blue-400/70 uppercase tracking-widest mb-1.5 block">Password</label>
            <div className="relative">
              <input type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleSubmit()}
                placeholder="••••••••"
                className="w-full px-4 py-3 pr-11 rounded-xl text-sm text-white placeholder-gray-600 outline-none border border-blue-900/40 focus:border-blue-500/60 transition-colors"
                style={{background:"rgba(255,255,255,0.04)"}} />
              <button type="button" onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-400/50 hover:text-blue-400 transition-colors">
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 4.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {error && (
            <motion.p initial={{opacity:0}} animate={{opacity:1}}
              className="text-red-400 text-xs text-center py-2 px-3 rounded-lg border border-red-900/30"
              style={{background:"rgba(239,68,68,0.08)"}}>
              {error}
            </motion.p>
          )}

          <motion.button onClick={handleSubmit} disabled={loading || !email || !password}
            whileTap={{scale:0.97}}
            className="w-full py-3 rounded-xl text-sm font-semibold text-white mt-2 transition-all disabled:opacity-40"
            style={{background: "linear-gradient(135deg, #4f8ef7, #a78bfa)"}}>
            {loading ? "Signing in..." : "Sign In"}
          </motion.button>
        </div>

        <p className="text-center text-xs text-gray-600 mt-6">ReKnew Data & AI · Internal Tool</p>
      </motion.div>
    </div>
  );
}
